import { useEffect, useState } from "react";
import { supabase, getAuthToken } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import { motion, AnimatePresence } from "framer-motion";
import {
  Database,
  Filter,
  Pencil,
  RefreshCw,
  Save,
  Search,
  Sparkles,
  Trash2,
  Wand2,
  X,
} from "lucide-react";

interface Entry {
  id: string;
  key: string;
  value: string;
  type?: string | null;
  aliases?: string[] | string | null;
  praticien_id: string;
  twilio_numero: string;
  technical_data: boolean;
}

interface GroupedEntries {
  praticien_id: string;
  label: string;
  twilio_numero: string;
  entries: Entry[];
}

interface Example {
  id: string;
  praticien_id: string;
  question: string;
  reponse: string;
  is_validated: boolean;
  created_at: string;
  confirmed_at?: string | null;
}

interface GroupedExamples {
  praticien_id: string;
  label: string;
  examples: Example[];
}

type KnowledgeFilter = "knowledge" | "technical" | "all";

const BACKEND_URL = "https://whatsapp-healthcare.onrender.com";

function getAliasList(aliases: Entry["aliases"]) {
  if (Array.isArray(aliases)) return aliases.map(String).filter(Boolean);
  if (typeof aliases === "string") {
    try {
      const parsed = JSON.parse(aliases);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch {
      return aliases
        .split(",")
        .map((alias) => alias.trim())
        .filter(Boolean);
    }
    return [];
  }
  return [];
}

function formatAliases(aliases: Entry["aliases"]) {
  const list = getAliasList(aliases);
  if (list.length > 0) return list.join(", ");
  return "";
}

function normalizeEditableAliases(value: string) {
  const seen = new Set<string>();
  return value
    .split(/[\n,]/)
    .map((alias) => alias.trim())
    .filter((alias) => {
      if (!alias || seen.has(alias)) return false;
      seen.add(alias);
      return true;
    });
}

function PractitionerRow({ nom, existing, twilioNumero, user, fetchEntries }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isOnLeave, setIsOnLeave] = useState(!!existing);
  const [saved, setSaved] = useState(false);

  // 🧠 Supprime le congé
  const removeLeave = async () => {
    const { error } = await supabase
      .from("clinic_knowledge")
      .delete()
      .eq("key", `conges_${nom}`)
      .eq("praticien_id", user.id);

    if (error) {
      console.error("❌ Erreur suppression congé:", error.message);
      alert("Erreur lors de la suppression du congé.");
    } else {
      fetchEntries();
      setIsOnLeave(false);
    }
  };

  // ✅ Sauvegarde ou met à jour le congé
  const saveLeave = async () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates.");
      return;
    }

    const { error } = await supabase
      .from("clinic_knowledge")
      .upsert(
        {
          key: `conges_${nom}`,
          value: `from ${startDate} to ${endDate}`,
          praticien_id: user.id,
          twilio_numero: twilioNumero,
          technical_data: true,
        },
        { onConflict: ["key", "praticien_id"] } // 👈 ajoute cette ligne clé
      )
      .select(); // pour récupérer l’état en sortie et vérifier le succès

    if (error) {
      console.error("❌ Erreur insertion congé:", error.message);
      alert("Erreur lors de l’enregistrement des congés.");
      return;
    }

    // ✅ Feedback visuel et refresh
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
    setIsOnLeave(true);
    getAuthToken().then((token) => {
      fetch("https://whatsapp-healthcare.onrender.com/admin/generate_embeddings", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({ twilio_numero: twilioNumero }),
      }).catch((err) => console.error("[EMBEDDINGS] error regenerating embeddings:", err));
    });
    fetchEntries();
  };

  // 🔁 Recharge l’état s’il y a déjà un congé
  useEffect(() => {
    if (existing && existing.value) {
      const match = existing.value.match(/(\d{4}-\d{2}-\d{2}).*(\d{4}-\d{2}-\d{2})/);
      if (match) {
        setStartDate(match[1]);
        setEndDate(match[2]);
        setIsOnLeave(true);
      }
    }
  }, [existing]);

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b py-3 gap-3">
      <div>
        <p className="font-medium text-slate-800">{nom}</p>
        {isOnLeave && startDate && endDate && (
          <p className="text-sm text-slate-500 mt-1">
            Currently on leave: {startDate} → {endDate}
          </p>
        )}

        {isOnLeave && (
          <div className="flex flex-col sm:flex-row gap-2 mt-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-slate-300 rounded-lg px-2 py-1 text-sm"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-slate-300 rounded-lg px-2 py-1 text-sm"
            />
            <button
              onClick={saveLeave}
              className="bg-primary text-white px-3 py-1 rounded-lg text-sm"
            >
              Save
            </button>
          </div>
        )}

        {saved && (
          <p className="text-green-600 text-sm mt-2 animate-fade-in">✅ Leave saved!</p>
        )}
      </div>

      <div className="flex items-center gap-3">
        <span
          className={`text-sm font-medium ${
            isOnLeave ? "text-red-600" : "text-green-600"
          }`}
        >
          {isOnLeave ? "On leave" : "Active"}
        </span>
      
        {/* Switch stylé */}
        <button
          type="button"
          onClick={() => {
            const newValue = !isOnLeave;
            setIsOnLeave(newValue);
            if (!newValue) removeLeave();
          }}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 ${
            isOnLeave ? "bg-red-500" : "bg-green-500"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
              isOnLeave ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

function PractitionerHolidays({ user, entries, fetchEntries }) {
  const [praticiens, setPraticiens] = useState<string[]>([]);
  const [twilioNumero, setTwilioNumero] = useState("");
  const [loading, setLoading] = useState(true);
  const [conges, setConges] = useState<Record<string, any>>({});

  // 🧩 Charge les praticiens et le Twilio numéro
  useEffect(() => {
    const load = async () => {
      if (!user) return;
      setLoading(true);

      // 1️⃣ Récupère la ligne "Praticiens" de cette clinique
      const { data: row, error: errPrat } = await supabase
        .from("clinic_knowledge")
        .select("value, twilio_numero")
        .eq("praticien_id", user.id)
        .eq("key", "Praticiens")
        .maybeSingle();

      if (errPrat) {
        console.warn("Erreur chargement praticiens:", errPrat.message);
      }

      // 2️⃣ Parse la liste des praticiens
      let noms: string[] = [];
      if (row?.value) {
        try {
          const parsed =
            typeof row.value === "string" ? JSON.parse(row.value) : row.value;
          if (Array.isArray(parsed)) noms = parsed.map(String);
        } catch {
          const s = String(row.value)
            .replace(/^\[|\]$/g, "")
            .split(",")
            .map((x) => x.replace(/(^["'\s]+|["'\s]+$)/g, ""))
            .filter(Boolean);
          noms = s;
        }
      }
      setPraticiens(noms);

      // 3️⃣ Récupère le Twilio numéro
      let tw = "";
      const { data: prof } = await supabase
        .from("profiles")
        .select("twilio_numero")
        .eq("id", user.id)
        .maybeSingle();
      tw = prof?.twilio_numero || row?.twilio_numero || "";
      setTwilioNumero(tw);

      setLoading(false);
    };

    load();
  }, [user]);

  // 🧩 Charge les congés existants (technical_data = true)
  useEffect(() => {
    const loadConges = async () => {
      if (!user) return;

      const { data, error } = await supabase
        .from("clinic_knowledge")
        .select("key, value")
        .eq("praticien_id", user.id)
        .eq("technical_data", true)
        .ilike("key", "conges_%");

      if (error) {
        console.error("Erreur chargement congés:", error.message);
        return;
      }

      if (data && data.length > 0) {
        const map = data.reduce((acc, row) => {
          acc[row.key] = row;
          return acc;
        }, {});
        setConges(map);
      }
    };

    loadConges();
  }, [user]);

  // 🧩 Rendu principal (sans bloc externe blanc)
  return (
    <div className="flex flex-col justify-between h-full">
      <h2 className="flex items-center text-lg font-semibold text-slate-800 mb-4">
        <span className="mr-2 text-xl">🏖️</span>
        Practitioner holidays
      </h2>
  
      {loading ? (
        <p className="text-slate-500">Loading...</p>
      ) : praticiens.length === 0 ? (
        <p className="text-slate-500">No practitioner found for this clinic.</p>
      ) : (
        praticiens.map((nom) => (
          <PractitionerRow
            key={nom}
            nom={nom}
            existing={conges[`conges_${nom}`]}
            twilioNumero={twilioNumero}
            user={user}
            fetchEntries={fetchEntries}
          />
        ))
      )}
    </div>
  );
}

export default function Knowledge() {
  const user = useUser();

  const [entries, setEntries] = useState<Entry[]>([]);
  const [grouped, setGrouped] = useState<GroupedEntries[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedExamples, setExpandedExamples] = useState<string | null>(null);

  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const [pratOptions, setPratOptions] = useState<GroupedEntries[]>([]);
  const [selectedPrat, setSelectedPrat] = useState<GroupedEntries | null>(null);

  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [generatingMetadataId, setGeneratingMetadataId] = useState<string | null>(null);
  const [generatingEmbeddings, setGeneratingEmbeddings] = useState<string | null>(null);
  const [knowledgeFilter, setKnowledgeFilter] = useState<KnowledgeFilter>("all");
  const [knowledgeSearch, setKnowledgeSearch] = useState("");
  const [editingMetadataId, setEditingMetadataId] = useState<string | null>(null);
  const [savingMetadataId, setSavingMetadataId] = useState<string | null>(null);
  const [editType, setEditType] = useState("");
  const [editAliases, setEditAliases] = useState("");

  const [confirmId, setConfirmId] = useState<string | null>(null);

  // validated_examples
  const [examples, setExamples] = useState<Example[]>([]);
  const [editingExample, setEditingExample] = useState<string | null>(null);
  const [editReponse, setEditReponse] = useState("");
  const [confirmExampleId, setConfirmExampleId] = useState<string | null>(null);
  const [selectedExampleIds, setSelectedExampleIds] = useState<string[]>([]);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);
  const [recentQuestions, setRecentQuestions] = useState<string[]>([]);

  // --------------------------
  // Knowledge base
  // --------------------------
  const fetchEntries = async () => {
    if (!user) return;

    const baseSelect = "id, key, value, praticien_id, twilio_numero, technical_data";
    const ragV2Select = "id, key, value, type, aliases, praticien_id, twilio_numero, technical_data";

    const buildQuery = (selectColumns: string) => {
      let query = supabase
        .from("clinic_knowledge")
        .select(selectColumns)
        .order("key");

      if (user.role !== "admin")
        query = query.eq("praticien_id", user.id).eq("technical_data", false);

      return query;
    };

    let { data, error } = await buildQuery(ragV2Select);
    if (error && /type|aliases/i.test(error.message || "")) {
      console.warn("knowledge fetch V2 columns unavailable, falling back to V1:", error.message);
      const fallback = await buildQuery(baseSelect);
      data = fallback.data;
      error = fallback.error;
    }
    if (error) {
      console.error("knowledge fetch", error.message);
      return;
    }

    setEntries(data || []);
    if (user.role === "admin") groupByPraticien(data || []);
  };

  const fetchRecentQuestions = async (utilisateur?: string) => {
    if (!user) return;
    if (!utilisateur) return; // on doit savoir de quel patient il s'agit
  
    const { data, error } = await supabase
      .from("conversations")
      .select("contenu, created_at")
      .eq("role", "user")
      .eq("praticien_id", user.id)
      .eq("utilisateur", utilisateur)
      .order("created_at", { ascending: false })
      .limit(5);
  
    if (error) {
      console.error("fetchRecentQuestions error:", error.message);
      return;
    }
  
    // on stocke dans l'ordre chronologique (du plus ancien au plus récent)
    setRecentQuestions((data || []).reverse().map((m) => m.contenu));
  };

  const groupByPraticien = async (data: Entry[]) => {
    const map = new Map<string, GroupedEntries>();
    for (const entry of data) {
      if (!map.has(entry.praticien_id)) {
        const { data: cli } = await supabase
          .from("clients")
          .select("raison_sociale")
          .eq("twilio_numero", entry.twilio_numero)
          .single();
        map.set(entry.praticien_id, {
          praticien_id: entry.praticien_id,
          twilio_numero: entry.twilio_numero,
          label: cli?.raison_sociale || entry.twilio_numero,
          entries: [],
        });
      }
      map.get(entry.praticien_id)!.entries.push(entry);
    }

    const arr = Array.from(map.values());
    setGrouped(arr);
    setPratOptions(arr);
    setSelectedPrat(arr[0] || null);
  };

  const addEntry = async () => {
    if (!newKey.trim() || !newValue.trim() || !user) return;

    const praticienId =
      user.role === "admin" ? selectedPrat?.praticien_id : user.id;
    const numero =
      user.role === "admin" ? selectedPrat?.twilio_numero : user.twilio_numero;

    if (!praticienId || !numero) {
      alert("Praticien ou numéro manquant");
      return;
    }

    const { data: inserted, error } = await supabase
      .from("clinic_knowledge")
      .insert({
        key: newKey.trim(),
        value: newValue.trim(),
        praticien_id: praticienId,
        twilio_numero: numero,
      })
      .select("id")
      .single();

    if (error) {
      alert("Erreur d'insertion : " + error.message);
      return;
    }

    setNewKey("");
    setNewValue("");
    await invalidateClinicCache(numero);
    if (user.role === "admin") {
      regenerateEmbeddings(numero);
    } else if (inserted?.id) {
      await generateEntryEmbeddingV2(inserted.id);
    }
    fetchEntries();
  };

  const confirmDelete = async () => {
    if (!confirmId) return;

    const { data, error } = await supabase
      .from("clinic_knowledge")
      .delete()
      .eq("id", confirmId)
      .select();

    if (error) {
      alert("Erreur delete : " + error.message);
    } else if (!data || data.length === 0) {
      alert("Suppression non autorisée (RLS ?)");
    } else {
      fetchEntries();
    }

    setConfirmId(null);
  };

  const invalidateClinicCache = async (twilio: string) => {
    try {
      const token = await getAuthToken();
      await fetch(`${BACKEND_URL}/internal/cache/invalidate_clinic`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({ twilio_numero: twilio }),
      });
      console.log("[CACHE] clinic invalidated for", twilio);
    } catch (err) {
      console.error("[CACHE] error invalidating clinic cache:", err);
    }
  };

  const regenerateEmbeddings = async (twilio: string) => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${BACKEND_URL}/admin/generate_embeddings`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({ twilio_numero: twilio }),
      });
      if (!res.ok) throw new Error(await res.text());
      console.log("[EMBEDDINGS] regenerated for", twilio);
      return true;
    } catch (err) {
      console.error("[EMBEDDINGS] error regenerating embeddings:", err);
      return false;
    }
  };

  const regenerateEmbeddingsV2 = async (twilio: string, refreshMetadata = true) => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${BACKEND_URL}/admin/generate_embeddings_v2`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({ twilio_numero: twilio, refresh_metadata: refreshMetadata }),
      });
      if (!res.ok) throw new Error(await res.text());
      console.log("[EMBEDDINGS][V2] regenerated for", twilio);
      return true;
    } catch (err) {
      console.error("[EMBEDDINGS][V2] error regenerating embeddings:", err);
      return false;
    }
  };

  const generateEntryMetadata = async (id: string, force = true) => {
    setGeneratingMetadataId(id);
    try {
      const token = await getAuthToken();
      const res = await fetch(`${BACKEND_URL}/admin/clinic_knowledge/generate_metadata`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({ id, force }),
      });
      if (!res.ok) throw new Error(await res.text());
      await fetchEntries();
    } catch (err) {
      console.error("[KNOWLEDGE][METADATA] error:", err);
      alert("Error generating type and aliases.");
    } finally {
      setGeneratingMetadataId(null);
    }
  };

  const generateEntryEmbeddingV2 = async (id: string, force = true) => {
    try {
      const token = await getAuthToken();
      const res = await fetch(`${BACKEND_URL}/admin/clinic_knowledge/generate_embedding_v2`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token ?? "" },
        body: JSON.stringify({ id, force }),
      });
      if (!res.ok) throw new Error(await res.text());
      await fetchEntries();
    } catch (err) {
      console.error("[KNOWLEDGE][EMBEDDING_V2] error:", err);
      alert("Error generating embedding V2.");
    }
  };

  const startMetadataEdit = (entry: Entry) => {
    setEditingMetadataId(entry.id);
    setEditType(entry.type || "");
    setEditAliases(getAliasList(entry.aliases).join("\n"));
  };

  const cancelMetadataEdit = () => {
    setEditingMetadataId(null);
    setEditType("");
    setEditAliases("");
  };

  const updateKnowledgeMetadata = async (id: string) => {
    const entry = entries.find((e) => e.id === id);
    const normalizedType = editType.trim();
    const aliasesList = normalizeEditableAliases(editAliases);

    setSavingMetadataId(id);
    try {
      const { error } = await supabase
        .from("clinic_knowledge")
        .update({ type: normalizedType || null, aliases: aliasesList })
        .eq("id", id);

      if (error) {
        alert("Erreur update metadata : " + error.message);
        return;
      }

      if (entry?.twilio_numero) {
        await invalidateClinicCache(entry.twilio_numero);
      }
      await generateEntryEmbeddingV2(id, false);
      cancelMetadataEdit();
      await fetchEntries();
    } finally {
      setSavingMetadataId(null);
    }
  };

  const updateEntry = async (id: string) => {
    const { error } = await supabase
      .from("clinic_knowledge")
      .update({ value: editValue })
      .eq("id", id);
  
    if (error) {
      alert("Erreur update : " + error.message);
    } else {
      // 🔥 Récupère l'entrée modifiée pour savoir quel twilio vider
      const entry = entries.find((e) => e.id === id);
      if (entry?.twilio_numero) {
        await invalidateClinicCache(entry.twilio_numero);
        if (user.role === "admin") {
          regenerateEmbeddings(entry.twilio_numero);
        } else {
          await generateEntryEmbeddingV2(id);
        }
      }

      setEditing(null);
      setEditValue("");
      fetchEntries();
    }
  };

  // --------------------------
  // Validated examples
  // --------------------------
  const fetchExamples = async () => {
    if (!user) return;
    let query = supabase
      .from("validated_examples")
      .select(
        "id, praticien_id, question, reponse, is_validated, created_at, confirmed_at"
      )
      .order("created_at", { ascending: false });

    if (user.role !== "admin") query = query.eq("praticien_id", user.id);

    const { data, error } = await query;
    if (error) {
      console.error("fetchExamples error:", error.message);
      return;
    }
    setExamples(data || []);
  };

  const updateExample = async (id: string) => {
    const { error } = await supabase
      .from("validated_examples")
      .update({ reponse: editReponse })
      .eq("id", id);

    if (error) alert("Erreur update : " + error.message);
    else {
      setEditingExample(null);
      setEditReponse("");
      fetchExamples();
    }
  };

  const confirmExample = async (id: string) => {
    const { error } = await supabase
      .from("validated_examples")
      .update({ is_validated: true, confirmed_at: new Date().toISOString() })
      .eq("id", id);

    if (error) alert("Erreur validation : " + error.message);
    else {
      fetchExamples();
      window.dispatchEvent(new Event("examples-updated")); // notify TopBar
    }
  };

  const deleteExample = async (id: string) => {
    const { error } = await supabase.from("validated_examples").delete().eq("id", id);

    if (error) alert("Erreur delete : " + error.message);
    else {
      fetchExamples();
      window.dispatchEvent(new Event("examples-updated")); // notify TopBar
    }
    setConfirmExampleId(null);
  };

  const toggleExampleSelection = (id: string) => {
    setSelectedExampleIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleAllExamplesSelection = (examplesList: Example[]) => {
    const visibleExampleIds = examplesList.map((ex) => ex.id);
    const allVisibleSelected =
      visibleExampleIds.length > 0 &&
      visibleExampleIds.every((id) => selectedExampleIds.includes(id));

    setSelectedExampleIds((prev) => {
      if (allVisibleSelected) {
        return prev.filter((id) => !visibleExampleIds.includes(id));
      }

      const next = new Set(prev);
      visibleExampleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const bulkDeleteExamples = async () => {
    if (selectedExampleIds.length === 0) {
      setConfirmBulkDeleteOpen(false);
      return;
    }

    const { error } = await supabase
      .from("validated_examples")
      .delete()
      .in("id", selectedExampleIds);

    if (error) {
      alert("Erreur delete : " + error.message);
      return;
    }

    await fetchExamples();
    window.dispatchEvent(new Event("examples-updated"));
    setSelectedExampleIds([]);
    setConfirmBulkDeleteOpen(false);
  };

  useEffect(() => {
    setSelectedExampleIds((prev) => {
      const availableIds = new Set(examples.map((ex) => ex.id));
      return prev.filter((id) => availableIds.has(id));
    });
  }, [examples]);

  useEffect(() => {
    fetchEntries();
    fetchExamples();
  }, [user]);

  if (!user) return <div className="p-6 text-slate-500">Chargement...</div>;

  const hasPendingExamples = examples.some((ex) => !ex.is_validated);

  const getPractitionerLabel = (praticienId: string) =>
    pratOptions.find((opt) => opt.praticien_id === praticienId)?.label ||
    grouped.find((opt) => opt.praticien_id === praticienId)?.label ||
    praticienId;

  const groupExamplesByPraticien = (examplesList: Example[]): GroupedExamples[] => {
    const map = new Map<string, GroupedExamples>();

    for (const example of examplesList) {
      if (!map.has(example.praticien_id)) {
        map.set(example.praticien_id, {
          praticien_id: example.praticien_id,
          label: getPractitionerLabel(example.praticien_id),
          examples: [],
        });
      }

      map.get(example.praticien_id)!.examples.push(example);
    }

    return Array.from(map.values());
  };

  const groupedExamples =
    user.role === "admin" ? groupExamplesByPraticien(examples) : [];

  // --------------------------
  // Render knowledge (responsive)
  // --------------------------
  const renderKnowledgeTable = (entriesList: Entry[]) => {
    const twilio = entriesList?.[0]?.twilio_numero;
    const knowledgeEntries = entriesList.filter((entry) => !entry.technical_data);
    const technicalEntries = entriesList.filter((entry) => entry.technical_data);
    const missingMetadataCount = knowledgeEntries.filter(
      (entry) => !entry.type || getAliasList(entry.aliases).length === 0
    ).length;
    const normalizedQuery = knowledgeSearch.trim().toLowerCase();
    const baseEntries =
      user.role !== "admin"
        ? knowledgeEntries
        : knowledgeFilter === "technical"
          ? technicalEntries
          : knowledgeFilter === "all"
            ? entriesList
            : knowledgeEntries;
    const visibleEntries = baseEntries.filter((entry) => {
      if (!normalizedQuery) return true;
      const haystack = [
        entry.key,
        entry.value,
        entry.type || "",
        formatAliases(entry.aliases),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedQuery);
    });
    const filterOptions: Array<{ value: KnowledgeFilter; label: string; count: number }> = [
      { value: "knowledge", label: "Knowledge", count: knowledgeEntries.length },
      { value: "technical", label: "Technical", count: technicalEntries.length },
      { value: "all", label: "All", count: entriesList.length },
    ];
    const renderAliasesPreview = (entry: Entry) => {
      const aliases = getAliasList(entry.aliases);
      if (aliases.length === 0) {
        return <span className="text-slate-400">-</span>;
      }

      const preview = aliases.slice(0, 2);
      return (
        <div className="flex flex-wrap gap-1">
          {preview.map((alias) => (
            <span
              key={`${entry.id}-${alias}`}
              title={alias}
              className="max-w-[11rem] truncate rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600"
            >
              {alias}
            </span>
          ))}
          {aliases.length > preview.length && (
            <details className="relative">
              <summary className="cursor-pointer list-none rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 hover:bg-slate-200">
                +{aliases.length - preview.length}
              </summary>
              <div className="absolute right-0 z-20 mt-2 max-h-48 w-72 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 shadow-xl">
                <div className="flex flex-wrap gap-1.5">
                  {aliases.map((alias) => (
                    <span
                      key={`${entry.id}-full-${alias}`}
                      className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-600"
                    >
                      {alias}
                    </span>
                  ))}
                </div>
              </div>
            </details>
          )}
        </div>
      );
    };

    return (
    <div className="mt-4">
      {/* Header with Generate Embeddings button (admin only) */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-semibold">🌍 Global knowledge</h2>
      
        {/* Bouton visible uniquement pour admin */}
        {user.role === "admin" && (
          <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            title="Generate legacy embeddings V1"
            onClick={async () => {
              if (!twilio) {
                alert("No twilio number found for this clinic.");
                return;
              }
          
              try {
                const token = await getAuthToken();
                const res = await fetch(`${BACKEND_URL}/admin/generate_embeddings`, {
                  method: "POST",
                  headers: { "Content-Type": "application/json", Authorization: token ?? "" },
                  body: JSON.stringify({ twilio_numero: twilio }),
                });
          
                if (!res.ok) {
                  console.error("Embeddings generation failed", await res.text());
                  alert("❌ Error generating embeddings (backend returned an error).");
                  return;
                }
          
                alert("✅ Embeddings generated successfully!");
              } catch (err) {
                console.error(err);
                alert("❌ Network error. Cannot reach backend.");
              }
            }}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-slate-800 px-3 text-sm font-medium text-white shadow-sm hover:bg-slate-900"
          >
            <Database className="h-4 w-4" aria-hidden="true" />
            ⚡ Generate embeddings
            <span className="ml-1">V1</span>
          </button>
          <button
            type="button"
            title="Generate enriched embeddings V2"
            onClick={async () => {
              if (!twilio) {
                alert("No twilio number found for this clinic.");
                return;
              }
              try {
                setGeneratingEmbeddings(`v2:${twilio}`);
                const ok = await regenerateEmbeddingsV2(twilio, true);
                if (!ok) {
                  alert("Error generating embeddings V2.");
                  return;
                }
                alert("Embeddings V2 generated successfully.");
                await fetchEntries();
              } catch (err) {
                console.error(err);
                alert("Network error. Cannot reach backend.");
              } finally {
                setGeneratingEmbeddings(null);
              }
            }}
            disabled={generatingEmbeddings === `v2:${twilio}`}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-indigo-600 px-3 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Generate embeddings V2
          </button>
          </div>
        )}
      </div>

      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2 text-xs">
            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 font-medium text-slate-600">
              {knowledgeEntries.length} knowledge
            </span>
            {user.role === "admin" && (
              <>
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 font-medium text-slate-600">
                  {technicalEntries.length} technical
                </span>
                {missingMetadataCount > 0 && (
                  <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 font-medium text-amber-700">
                    {missingMetadataCount} missing metadata
                  </span>
                )}
              </>
            )}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative w-full sm:w-72">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                aria-label="Search knowledge"
                value={knowledgeSearch}
                onChange={(e) => setKnowledgeSearch(e.target.value)}
                placeholder="Search key, value, type, aliases"
                className="h-9 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-800 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
              />
            </div>

            {user.role === "admin" && (
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" aria-hidden="true" />
                <div className="inline-flex rounded-md border border-slate-200 bg-white p-1">
                  {filterOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      aria-pressed={knowledgeFilter === option.value}
                      onClick={() => setKnowledgeFilter(option.value)}
                      className={`rounded px-3 py-1.5 text-xs font-medium ${
                        knowledgeFilter === option.value
                          ? "bg-slate-900 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {option.label}
                      <span className="ml-1 opacity-75">{option.count}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {visibleEntries.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-500">
          No knowledge line matches the current view.
        </div>
      )}

      {/* Desktop */}
      <table className="hidden w-full table-fixed text-sm text-left border-t border-gray-200 divide-y divide-gray-100 sm:table">
        <thead className="bg-white text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="w-[16%] py-2 px-3">Key</th>
            <th className="w-[34%] py-2 px-3">Value</th>
            {user.role === "admin" && <th className="w-[12%] py-2 px-3">Type</th>}
            {user.role === "admin" && <th className="w-[20%] py-2 px-3">Aliases</th>}
            {user.role === "admin" && <th className="w-[6%] py-2 px-3 text-center">Tech</th>}
            <th className="w-[12%] py-2 px-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {visibleEntries.map((entry) => (
            <tr key={entry.id} className="align-top hover:bg-slate-50/70">
              <td className="py-3 px-3 font-mono text-[13px] font-semibold leading-5 text-slate-900 break-all">
                {entry.key}
              </td>
              <td className="py-3 px-3">
                {editing === entry.id ? (
                  <textarea
                    className="min-h-[88px] w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm leading-6 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                  />
                ) : (
                  <div className="max-h-24 overflow-y-auto whitespace-pre-wrap break-words leading-6 text-slate-700">
                    {entry.value}
                  </div>
                )}
              </td>
              {user.role === "admin" && (
                <td className="py-3 px-3 text-slate-600">
                  {editingMetadataId === entry.id ? (
                    <input
                      className="h-8 w-full rounded-md border border-slate-300 px-2 text-xs outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                      value={editType}
                      onChange={(e) => setEditType(e.target.value)}
                      placeholder="clinic_address"
                    />
                  ) : entry.type ? (
                    <span className="inline-flex max-w-full rounded-full bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 break-all">
                      {entry.type}
                    </span>
                  ) : (
                    <span className="text-slate-400">-</span>
                  )}
                </td>
              )}
              {user.role === "admin" && (
                <td className="py-3 px-3 text-slate-600">
                  {editingMetadataId === entry.id ? (
                    <textarea
                      className="min-h-[84px] w-full resize-y rounded-md border border-slate-300 px-2 py-2 text-xs leading-5 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                      value={editAliases}
                      onChange={(e) => setEditAliases(e.target.value)}
                      placeholder="One alias per line"
                    />
                  ) : (
                    renderAliasesPreview(entry)
                  )}
                </td>
              )}
              {user.role === "admin" && (
                <td className="py-3 px-3 text-center">
                  <input
                    type="checkbox"
                    checked={entry.technical_data}
                    onChange={async (e) => {
                      await supabase
                        .from("clinic_knowledge")
                        .update({ technical_data: e.target.checked })
                        .eq("id", entry.id);
                      fetchEntries();
                    }}
                  />
                </td>
              )}
              <td className="py-3 px-3">
                <div className="flex flex-wrap justify-end gap-2">
                {editing === entry.id ? (
                  <>
                    <button
                      type="button"
                      title="Save"
                      onClick={() => updateEntry(entry.id)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 text-xs font-medium text-white hover:bg-emerald-700"
                    >
                      <Save className="h-3.5 w-3.5" aria-hidden="true" />
                      ✔ Save
                    </button>
                    <button
                      type="button"
                      title="Cancel"
                      onClick={() => {
                        setEditing(null);
                        setEditValue("");
                      }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                      ✖ Cancel
                    </button>
                  </>
                ) : editingMetadataId === entry.id ? (
                  <>
                    <button
                      type="button"
                      title="Save metadata"
                      onClick={() => updateKnowledgeMetadata(entry.id)}
                      disabled={savingMetadataId === entry.id}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {savingMetadataId === entry.id ? (
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      ) : (
                        <Save className="h-3.5 w-3.5" aria-hidden="true" />
                      )}
                      Save metadata
                    </button>
                    <button
                      type="button"
                      title="Cancel metadata edit"
                      onClick={cancelMetadataEdit}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      <X className="h-3.5 w-3.5" aria-hidden="true" />
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    {user.role === "admin" && (
                      <>
                        <button
                          type="button"
                          title="Generate type and aliases"
                          onClick={() => generateEntryMetadata(entry.id, true)}
                          disabled={generatingMetadataId === entry.id}
                          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-indigo-200 bg-indigo-50 px-2.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {generatingMetadataId === entry.id ? (
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                          ) : (
                            <Wand2 className="h-3.5 w-3.5" aria-hidden="true" />
                          )}
                          {generatingMetadataId === entry.id ? "Generating..." : "AI metadata"}
                        </button>
                        <button
                          type="button"
                          title="Edit type and aliases"
                          onClick={() => startMetadataEdit(entry)}
                          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                        >
                          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                          Edit metadata
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      title="Modify"
                      onClick={() => {
                        setEditing(entry.id);
                        setEditValue(entry.value);
                      }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      Edit
                    </button>
                    <button
                      type="button"
                      title="Delete"
                      onClick={() => setConfirmId(entry.id)}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md border border-red-200 bg-red-50 px-2.5 text-xs font-medium text-red-700 hover:bg-red-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      Delete
                    </button>
                  </>
                )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile */}
      <div className="sm:hidden space-y-3">
        {visibleEntries.map((entry) => (
          <div key={entry.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold">Key</p>
            <p>{entry.key}</p>
            <p className="text-sm font-semibold mt-2">Value</p>
            {editing === entry.id ? (
              <textarea
                className="border px-3 py-2 rounded w-full min-h-[60px] resize-y"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
              />
            ) : (
              <p>{entry.value}</p>
            )}
            {user.role === "admin" && (
              <>
                <p className="text-sm font-semibold mt-2">Type</p>
                {editingMetadataId === entry.id ? (
                  <input
                    className="mt-1 h-9 w-full rounded-md border border-slate-300 px-3 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    value={editType}
                    onChange={(e) => setEditType(e.target.value)}
                    placeholder="clinic_address"
                  />
                ) : (
                  <p>{entry.type || "-"}</p>
                )}
                <p className="text-sm font-semibold mt-2">Aliases</p>
                {editingMetadataId === entry.id ? (
                  <textarea
                    className="mt-1 min-h-[90px] w-full resize-y rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                    value={editAliases}
                    onChange={(e) => setEditAliases(e.target.value)}
                    placeholder="One alias per line"
                  />
                ) : (
                  renderAliasesPreview(entry)
                )}
              </>
            )}
            <div className="mt-3 flex flex-wrap justify-end gap-2">
              {editing === entry.id ? (
                <>
                  <button
                    onClick={() => updateEntry(entry.id)}
                    className="text-green-600 hover:underline"
                  >
                    ✔ Save
                  </button>
                  <button
                    onClick={() => {
                      setEditing(null);
                      setEditValue("");
                    }}
                    className="text-slate-500 hover:underline"
                  >
                    ✖ Cancel
                  </button>
                </>
              ) : editingMetadataId === entry.id ? (
                <>
                  <button
                    onClick={() => updateKnowledgeMetadata(entry.id)}
                    disabled={savingMetadataId === entry.id}
                    className="text-green-600 hover:underline disabled:opacity-60"
                  >
                    {savingMetadataId === entry.id ? "Saving..." : "Save metadata"}
                  </button>
                  <button
                    onClick={cancelMetadataEdit}
                    className="text-slate-500 hover:underline"
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  {user.role === "admin" && (
                    <>
                      <button
                        onClick={() => generateEntryMetadata(entry.id, true)}
                        disabled={generatingMetadataId === entry.id}
                        className="text-indigo-600 hover:underline disabled:opacity-60"
                      >
                        {generatingMetadataId === entry.id ? "Generating..." : "AI metadata"}
                      </button>
                      <button
                        onClick={() => startMetadataEdit(entry)}
                        className="text-slate-700 hover:underline"
                      >
                        Edit metadata
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      setEditing(entry.id);
                      setEditValue(entry.value);
                    }}
                    className="text-blue-600 hover:underline"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => setConfirmId(entry.id)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
    );
  };

  // --------------------------
  // Render examples (responsive)
  // --------------------------
  const renderExamplesTable = (examplesList: Example[]) => {
    const visibleExampleIds = examplesList.map((ex) => ex.id);
    const selectedVisibleCount = visibleExampleIds.filter((id) =>
      selectedExampleIds.includes(id)
    ).length;
    const allVisibleExamplesSelected =
      visibleExampleIds.length > 0 && selectedVisibleCount === visibleExampleIds.length;

    return (
    <div className="mt-6">
      {/* Desktop */}
      <table className="hidden sm:table min-w-full text-sm text-left border-t border-gray-200 divide-y divide-gray-100">
        <thead className="text-slate-600">
          <tr>
            <th className="py-2 px-4">Question</th>
            <th className="py-2 px-4">Answer</th>
            <th className="py-2 px-4">Status</th>
            <th className="py-2 px-4 text-right">Actions</th>
            {user.role === "admin" && (
              <th className="py-2 px-4 text-center">
                Select
                <div className="mt-1 flex justify-center">
                  <input
                    type="checkbox"
                    checked={allVisibleExamplesSelected}
                    onChange={() => toggleAllExamplesSelection(examplesList)}
                    aria-label="Select all examples"
                  />
                </div>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {examplesList.map((ex) => (
            <tr key={ex.id}>
              <td className="py-2 px-4">
                {editingExample === ex.id ? (
                  <select
                    className="border px-3 py-2 rounded w-full"
                    value={ex.question}
                    onChange={async (e) => {
                      const newQuestion = e.target.value;
                      await supabase
                        .from("validated_examples")
                        .update({ question: newQuestion })
                        .eq("id", ex.id);
                      fetchExamples();
                    }}
                  >
                    <option value={ex.question}>{ex.question}</option>
                    {recentQuestions
                      .filter((q) => q !== ex.question)
                      .map((q, i) => (
                        <option key={i} value={q}>
                          {q}
                        </option>
                      ))}
                  </select>
                ) : (
                  ex.question
                )}
              </td>
              <td className="py-2 px-4">
                {editingExample === ex.id ? (
                  <textarea
                    className="border px-3 py-2 rounded w-full min-h-[80px] resize-y"
                    value={editReponse}
                    onChange={(e) => setEditReponse(e.target.value)}
                  />
                ) : (
                  ex.reponse
                )}
              </td>
              <td className="py-2 px-4 text-center">
                {!ex.is_validated ? (
                  <button
                    onClick={() => confirmExample(ex.id)}
                    className="px-3 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
                  >
                    To be confirmed
                  </button>
                ) : (
                  <div className="inline-block px-3 py-1 rounded-full bg-green-600 text-white text-sm font-medium">
                    Confirmed{" "}
                    {ex.confirmed_at &&
                      `on ${new Date(ex.confirmed_at).toLocaleDateString()}`}
                  </div>
                )}
              </td>
              <td className="py-2 px-4 text-right space-x-2">
                {editingExample === ex.id ? (
                  <>
                    <button
                      onClick={() => updateExample(ex.id)}
                      className="text-green-600 hover:underline"
                    >
                      ✔ Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingExample(null);
                        setEditReponse("");
                      }}
                      className="text-slate-500 hover:underline"
                    >
                      ✖ Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => {
                        setEditingExample(ex.id);
                        setEditReponse(ex.reponse);
                        fetchRecentQuestions(ex.utilisateur); // ✅ charge les 5 derniers messages de ce patient
                      }}
                      className="text-blue-600 hover:underline"
                    >
                      Modify
                    </button>
                    <button
                      onClick={() => setConfirmExampleId(ex.id)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </>
                )}
              </td>
              {user.role === "admin" && (
                <td className="py-2 px-4 text-center">
                  <input
                    type="checkbox"
                    checked={selectedExampleIds.includes(ex.id)}
                    onChange={() => toggleExampleSelection(ex.id)}
                    aria-label={`Select example ${ex.id}`}
                  />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Mobile */}
      <div className="sm:hidden space-y-4">
        {examplesList.map((ex) => (
          <div key={ex.id} className="border rounded-lg p-4 shadow-sm">
            <p className="text-sm font-semibold">Question</p>
            {editingExample === ex.id ? (
              <select
                className="border px-3 py-2 rounded w-full mb-2"
                value={ex.question}
                onChange={async (e) => {
                  const newQuestion = e.target.value;
                  await supabase
                    .from("validated_examples")
                    .update({ question: newQuestion })
                    .eq("id", ex.id);
                  fetchExamples();
                }}
              >
                <option value={ex.question}>{ex.question}</option>
                {recentQuestions
                  .filter((q) => q !== ex.question)
                  .map((q, i) => (
                    <option key={i} value={q}>
                      {q}
                    </option>
                  ))}
              </select>
            ) : (
              <p className="mb-2">{ex.question}</p>
            )}
            <p className="text-sm font-semibold">Answer</p>
            {editingExample === ex.id ? (
              <textarea
                className="border px-3 py-2 rounded w-full min-h-[80px] resize-y"
                value={editReponse}
                onChange={(e) => setEditReponse(e.target.value)}
              />
            ) : (
              <p className="mb-2">{ex.reponse}</p>
            )}
            <p className="text-sm font-semibold">Status</p>
            {!ex.is_validated ? (
              <button
                onClick={() => confirmExample(ex.id)}
                className="mb-2 px-3 py-1 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm"
              >
                To be confirmed
              </button>
            ) : (
              <div className="mb-2 inline-block px-3 py-1 rounded-full bg-green-600 text-white text-sm font-medium">
                Confirmed{" "}
                {ex.confirmed_at &&
                  `on ${new Date(ex.confirmed_at).toLocaleDateString()}`}
              </div>
            )}
            <div className="flex space-x-3 pt-2">
              {editingExample === ex.id ? (
                <>
                  <button
                    onClick={() => updateExample(ex.id)}
                    className="text-green-600 hover:underline"
                  >
                    ✔ Save
                  </button>
                  <button
                    onClick={() => {
                      setEditingExample(null);
                      setEditReponse("");
                    }}
                    className="text-slate-500 hover:underline"
                  >
                    ✖ Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setEditingExample(ex.id);
                      setEditReponse(ex.reponse);
                      fetchRecentQuestions(ex.utilisateur);
                    }}
                    className="text-blue-600 hover:underline"
                  >
                    Modify
                  </button>

                  <button
                    onClick={() => setConfirmExampleId(ex.id)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
    );
  };

  // --------------------------
  // Render main
  // --------------------------
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 font-sans text-slate-800 bg-[#f9fbfd] min-h-full">
      <h1 className="text-2xl font-bold text-slate-800 mb-6">🧠 Knowledge base</h1>

      {hasPendingExamples && (
        <div className="mb-6 p-4 bg-yellow-100 text-yellow-800 rounded-lg">
          ⚠️ You have new learnings to validate or delete
        </div>
      )}

      {/* Examples */}
      <div className="bg-white rounded-2xl shadow border border-slate-100 p-6 mt-8">
        <div className="flex items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-semibold">✅ Validated examples (AI learning)</h2>
          {user.role === "admin" && selectedExampleIds.length > 0 && (
            <button
              onClick={() => setConfirmBulkDeleteOpen(true)}
              className="px-3 py-1.5 rounded-lg bg-red-600 text-white hover:bg-red-700 text-sm"
            >
              Delete selected ({selectedExampleIds.length})
            </button>
          )}
        </div>
        {user.role === "admin" ? (
          groupedExamples.length === 0 ? (
            <p className="text-slate-500 mt-4">No examples yet.</p>
          ) : (
            groupedExamples.map((group) => (
              <div key={group.praticien_id} className="mb-6">
                <button
                  className="text-left w-full text-lg font-semibold text-primary hover:underline"
                  onClick={() =>
                    setExpandedExamples(
                      expandedExamples === group.praticien_id ? null : group.praticien_id
                    )
                  }
                >
                  {expandedExamples === group.praticien_id ? "\u25BC" : "\u25B6"} {group.label} (
                  {group.examples.length})
                </button>
                {expandedExamples === group.praticien_id &&
                  renderExamplesTable(group.examples)}
              </div>
            ))
          )
        ) : (
          renderExamplesTable(examples)
        )}
      </div>

      <br />

      {/* 🧱 Section combinée : deux cartes équilibrées */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        {/* 🧩 Bloc ajout de connaissance */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col justify-between hover:shadow-md transition-shadow duration-300">
          <h2 className="flex items-center text-lg font-semibold text-slate-800 mb-4">
            <span className="text-violet-500 mr-2 text-xl">➕</span>
            Add new knowledge
          </h2>
      
          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <input
              className="border border-slate-300 rounded-xl px-3 py-2 w-full sm:w-1/3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Key"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
            />
            <input
              className="border border-slate-300 rounded-xl px-3 py-2 w-full sm:w-2/3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
            />
          </div>
      
          {user.role === "admin" && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-600 mb-1">
                Concerned clinic
              </label>
              <select
                className="border border-slate-300 rounded-xl px-3 py-2 w-full sm:w-1/2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={selectedPrat?.praticien_id || ""}
                onChange={(e) => {
                  const p = pratOptions.find(
                    (opt) => opt.praticien_id === e.target.value
                  );
                  setSelectedPrat(p || null);
                }}
              >
                {pratOptions.map((opt) => (
                  <option key={opt.praticien_id} value={opt.praticien_id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}
      
          <button
            onClick={addEntry}
            className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white font-semibold px-5 py-2 rounded-xl transition-colors duration-200 self-start"
          >
            Add new knowledge
          </button>
        </div>
      
        {/* 🧩 Bloc congés praticiens — même style visuel */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow duration-300">
          <PractitionerHolidays
            user={user}
            entries={entries}
            fetchEntries={fetchEntries}
          />
        </div>
      </div>

      {/* Knowledge */}
      <div className="bg-white rounded-2xl shadow border border-slate-100 p-6">
        {user.role === "admin"
          ? grouped.map((g) => (
              <div key={g.praticien_id} className="mb-6">
                <button
                  className="text-left w-full text-lg font-semibold text-primary hover:underline"
                  onClick={() =>
                    setExpanded(
                      expanded === g.praticien_id ? null : g.praticien_id
                    )
                  }
                >
                  {expanded === g.praticien_id ? "▼" : "▶"} {g.label}
                </button>
                {expanded === g.praticien_id && renderKnowledgeTable(g.entries)}
              </div>
            ))
          : renderKnowledgeTable(entries)}
      </div>

      {/* Modale delete knowledge */}
      <AnimatePresence>
        {confirmId && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <h2 className="text-lg font-semibold text-slate-800 mb-4">
                ❗ Confirm delete
              </h2>
              <p className="text-slate-600 mb-6">
                Do you really want to delete this knowledge line ?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmId(null)}
                  className="px-4 py-2 rounded-lg border text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modale delete example */}
      <AnimatePresence>
        {confirmExampleId && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <h2 className="text-lg font-semibold text-slate-800 mb-4">
                ❗ Confirm delete
              </h2>
              <p className="text-slate-600 mb-6">
                Do you really want to delete this example ?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmExampleId(null)}
                  className="px-4 py-2 rounded-lg border text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteExample(confirmExampleId)}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modale bulk delete examples */}
      <AnimatePresence>
        {confirmBulkDeleteOpen && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-lg p-6 w-full max-w-sm"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <h2 className="text-lg font-semibold text-slate-800 mb-4">
                ❗ Confirm bulk delete
              </h2>
              <p className="text-slate-600 mb-6">
                Do you really want to delete the selected examples?
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmBulkDeleteOpen(false)}
                  className="px-4 py-2 rounded-lg border text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  onClick={bulkDeleteExamples}
                  className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
